// Offline mutation queue of the employee: writes that failed for lack of network are stored in
// IndexedDB (idb-keyval) and replayed in order once the browser is online again. Items carry the
// id of the user who queued them; only the current user's items are visible and replayed, the
// whole store is wiped on logout. Every read-modify-write re-reads the store inside one promise
// chain (and other tabs are told through a BroadcastChannel), so two tabs never persist a stale
// array; the replay itself additionally runs under a cross-tab lock.
import { get as idbGet, set as idbSet } from "idb-keyval";
import { ApiError, api, NetworkError } from "@/api/client";

export const QUEUED_MUTATION_KINDS = [
  "entry_create",
  "entry_update",
  "entry_delete",
  "entry_submit",
  "entry_withdraw",
  "entry_correct",
  "week_submit",
  "vacation_create",
  "azk_create",
] as const;
export type QueuedMutationKind = (typeof QUEUED_MUTATION_KINDS)[number];

export type QueuedMutation = {
  id: string;
  // The user who queued it; items of other users are hidden and dropped at replay.
  userId: string | null;
  kind: QueuedMutationKind;
  method: "POST" | "PUT" | "DELETE";
  // May contain `{client:<clientId>}` for the id of an entry whose create is still queued.
  url: string;
  body?: unknown;
  createdAt: string;
  // Server id of the entry the mutation targets (entry_* kinds), once known.
  entryId?: string;
  // `clientId` of the queued create this item creates or depends on.
  clientId?: string;
  // Short human-readable label of the payload ("Mi. 16.09. 07:00 – 15:00") for notices.
  summary: string;
};

// A queued item the server rejected (4xx): kept until the employee dismisses it.
export type OfflineFailure = {
  id: string;
  userId: string | null;
  kind: QueuedMutationKind;
  summary: string;
  message: string;
  // Rule codes of a validation failure (`ApiError.fieldErrors`), translated by the banner.
  errorCodes: string[];
  failedAt: string;
  // `foreign_user`: the item belonged to someone else who used this device and was dropped
  // unsent when the current user logged in (shared phones); the banner says so once.
  reason?: "foreign_user";
};

// Why the last replay stopped before the queue was empty.
export type ReplayStop = "unauthorized" | "unavailable" | "locked";

export type ReplayResult = {
  sent: number;
  failed: OfflineFailure[];
  stopped: ReplayStop | null;
};

// Persistence behind the queue; tests swap in a Map.
export type QueueStore = {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
};

export const QUEUE_KEY = "zt-offline-queue";
export const FAILURES_KEY = "zt-offline-failures";
const CHANNEL = "zt-offline-queue";
const REPLAY_LOCK = "zt-offline-replay";
const LOCK_TTL_MS = 60_000;

const idbStore: QueueStore = {
  get: (key) => idbGet(key),
  set: (key, value) => idbSet(key, value),
};

export type SendMutation = (item: QueuedMutation) => Promise<unknown>;

const defaultSend: SendMutation = (item) => api(item.method, item.url, { body: item.body });

// Runs `fn` unless another tab holds the replay lock (Web Locks, else a localStorage lease).
export type ReplayLock = <T>(fn: () => Promise<T>, skipped: T) => Promise<T>;

const defaultLock: ReplayLock = async (fn, skipped) => {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (locks?.request) {
    return locks.request(REPLAY_LOCK, { ifAvailable: true }, (lock) =>
      lock ? fn() : Promise.resolve(skipped),
    );
  }
  let leased = false;
  try {
    const held = Number(localStorage.getItem(REPLAY_LOCK));
    if (held && Date.now() - held < LOCK_TTL_MS) return skipped;
    localStorage.setItem(REPLAY_LOCK, String(Date.now()));
    leased = true;
  } catch {
    // No storage: nothing to coordinate with.
  }
  try {
    return await fn();
  } finally {
    if (leased) {
      try {
        localStorage.removeItem(REPLAY_LOCK);
      } catch {
        // ignore
      }
    }
  }
};

type Listener = () => void;

export type OfflineQueue = {
  // Loads the stored items into memory; safe to call more than once.
  init(): Promise<void>;
  // The user whose items are visible; `null` hides everything (logged out).
  setUser(userId: string | null): void;
  add(input: Omit<QueuedMutation, "id" | "createdAt" | "userId">): Promise<QueuedMutation>;
  // Drops an item and everything that depends on it (a queued create's submit).
  remove(id: string): Promise<void>;
  dismissFailure(id: string): Promise<void>;
  // Wipes the queue and the notices of every user (logout).
  clear(): Promise<void>;
  // Sends the current user's items in order; only one replay runs at a time (across tabs).
  replay(): Promise<ReplayResult>;
  isReplaying(): boolean;
  // Why the last replay left items behind, until the next attempt.
  lastStop(): ReplayStop | null;
  items(): readonly QueuedMutation[];
  failures(): readonly OfflineFailure[];
  subscribe(listener: Listener): () => void;
};

export function placeholderFor(clientId: string): string {
  return `{client:${clientId}}`;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Fills in the server id of a replayed create in the items that waited for it.
export function resolveClientId(
  items: QueuedMutation[],
  clientId: string,
  entryId: string,
): QueuedMutation[] {
  const placeholder = placeholderFor(clientId);
  return items.map((item) =>
    item.clientId === clientId && item.entryId === undefined
      ? { ...item, entryId, url: item.url.replaceAll(placeholder, entryId) }
      : item,
  );
}

function dependents(items: QueuedMutation[], removed: QueuedMutation): QueuedMutation[] {
  if (removed.kind !== "entry_create" || !removed.clientId) return [];
  return items.filter((i) => i.id !== removed.id && i.clientId === removed.clientId);
}

// A 409 on a status transition means the transition already happened (the response of an
// earlier attempt was lost): done, not a failure.
const ALREADY_APPLIED_ON_CONFLICT: ReadonlySet<QueuedMutationKind> = new Set([
  "entry_submit",
  "entry_withdraw",
]);

export function createOfflineQueue(
  options: { store?: QueueStore; send?: SendMutation; lock?: ReplayLock; now?: () => Date } = {},
): OfflineQueue {
  const store = options.store ?? idbStore;
  const send = options.send ?? defaultSend;
  const lock = options.lock ?? defaultLock;
  const now = options.now ?? (() => new Date());
  let all: QueuedMutation[] = [];
  let allFailures: OfflineFailure[] = [];
  let userId: string | null = null;
  let mine: QueuedMutation[] = [];
  let myFailures: OfflineFailure[] = [];
  let replaying = false;
  let stop: ReplayStop | null = null;
  let chain: Promise<unknown> = Promise.resolve();
  const listeners = new Set<Listener>();
  const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);

  const project = () => {
    mine = userId === null ? [] : all.filter((i) => i.userId === userId);
    myFailures = userId === null ? [] : allFailures.filter((f) => f.userId === userId);
  };

  const emit = () => {
    project();
    for (const listener of listeners) listener();
  };

  // Serialises every read-modify-write of the stored arrays.
  const locked = <T>(task: () => Promise<T>): Promise<T> => {
    const next = chain.then(task, task);
    chain = next.catch(() => undefined);
    return next;
  };

  // Always the stored truth: another tab may have changed it since the last read.
  const refresh = async () => {
    try {
      all = (await store.get<QueuedMutation[]>(QUEUE_KEY)) ?? [];
      allFailures = (await store.get<OfflineFailure[]>(FAILURES_KEY)) ?? [];
    } catch {
      all = [];
      allFailures = [];
    }
    emit();
  };

  channel?.addEventListener("message", () => void locked(refresh));

  // Written before React sees the change, so a navigation right after a click cannot lose it.
  const persist = async (key: string, value: unknown) => {
    try {
      await store.set(key, value);
    } catch {
      // Storage blocked: keep the in-memory state for this session.
    }
  };

  const persistItems = async (next: QueuedMutation[]) => {
    await persist(QUEUE_KEY, next);
    all = next;
    emit();
    channel?.postMessage("changed");
  };

  const persistFailures = async (next: OfflineFailure[]) => {
    await persist(FAILURES_KEY, next);
    allFailures = next;
    emit();
    channel?.postMessage("changed");
  };

  const dropWithNotice = (item: QueuedMutation, error: ApiError) =>
    locked(async () => {
      await refresh();
      // Dependents (the submit of a refused create) go with it; one notice per logical failure.
      const droppedIds = new Set([item.id, ...dependents(all, item).map((d) => d.id)]);
      const notice: OfflineFailure = {
        id: item.id,
        userId: item.userId,
        kind: item.kind,
        summary: item.summary,
        message: error.message,
        errorCodes: [...new Set(Object.values(error.fieldErrors))],
        failedAt: now().toISOString(),
      };
      await persistItems(all.filter((i) => !droppedIds.has(i.id)));
      await persistFailures([...allFailures, notice]);
      return notice;
    });

  const markSent = (item: QueuedMutation, response: unknown) =>
    locked(async () => {
      await refresh();
      let next = all.filter((i) => i.id !== item.id);
      const created = response as { id?: unknown } | null;
      if (item.kind === "entry_create" && item.clientId && typeof created?.id === "string")
        next = resolveClientId(next, item.clientId, created.id);
      await persistItems(next);
    });

  const replay = async (): Promise<ReplayResult> => {
    const result: ReplayResult = { sent: 0, failed: [], stopped: null };
    while (true) {
      const item = await locked(async () => {
        await refresh();
        const user = userId;
        // Items of other users (or of nobody) are never sent from this session. The user who
        // is logged in now gets one notice per dropped item, so nothing disappears silently.
        const foreign = all.filter((i) => user === null || i.userId !== user);
        if (foreign.length > 0) {
          const foreignIds = new Set(foreign.map((f) => f.id));
          await persistItems(all.filter((i) => !foreignIds.has(i.id)));
          if (user !== null) {
            const at = now().toISOString();
            await persistFailures([
              ...allFailures,
              ...foreign.map<OfflineFailure>((item) => ({
                id: item.id,
                userId: user,
                kind: item.kind,
                summary: item.summary,
                message: "",
                errorCodes: [],
                failedAt: at,
                reason: "foreign_user",
              })),
            ]);
          }
        }
        return all[0];
      });
      if (!item) return result;
      let response: unknown;
      try {
        response = await send(item);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          result.stopped = "unauthorized";
          return result;
        }
        if (
          error instanceof ApiError &&
          error.status === 409 &&
          ALREADY_APPLIED_ON_CONFLICT.has(item.kind)
        ) {
          response = null;
        } else if (error instanceof ApiError && error.status < 500) {
          // The server refused the payload: drop it with a notice so nothing disappears silently.
          result.failed.push(await dropWithNotice(item, error));
          continue;
        } else if (error instanceof ApiError || error instanceof NetworkError) {
          // 5xx or no network: keep everything for the next attempt.
          result.stopped = "unavailable";
          return result;
        } else {
          throw error;
        }
      }
      result.sent += 1;
      await markSent(item, response);
    }
  };

  return {
    init: () => locked(refresh),
    setUser: (next) => {
      if (next === userId) return;
      userId = next;
      emit();
    },
    add: (input) =>
      locked(async () => {
        await refresh();
        const item: QueuedMutation = {
          ...input,
          id: newId(),
          userId,
          createdAt: now().toISOString(),
        };
        await persistItems([...all, item]);
        return item;
      }),
    remove: (id) =>
      locked(async () => {
        await refresh();
        const removed = all.find((i) => i.id === id);
        if (!removed) return;
        const droppedIds = new Set([id, ...dependents(all, removed).map((d) => d.id)]);
        await persistItems(all.filter((i) => !droppedIds.has(i.id)));
      }),
    dismissFailure: (id) =>
      locked(async () => {
        await refresh();
        await persistFailures(allFailures.filter((f) => f.id !== id));
      }),
    clear: () =>
      locked(async () => {
        await persistItems([]);
        await persistFailures([]);
        stop = null;
      }),
    replay: async () => {
      if (replaying) return { sent: 0, failed: [], stopped: "locked" };
      replaying = true;
      emit();
      try {
        const result = await lock(replay, { sent: 0, failed: [], stopped: "locked" as const });
        if (result.stopped !== "locked") stop = result.stopped;
        return result;
      } finally {
        replaying = false;
        emit();
      }
    },
    isReplaying: () => replaying,
    lastStop: () => stop,
    items: () => mine,
    failures: () => myFailures,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// The app-wide queue (IndexedDB backed).
export const offlineQueue = createOfflineQueue();
